// ===========================================
// Template Manager Card Component
// ===========================================
// Manage which email templates are enabled/disabled

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TextField,
  InputAdornment,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEmailTemplates, toggleEmailTemplate, EmailTemplate } from '../../../api/emailBranding.api';
import { apiErrorMessage } from '../../../utils/apiError';
import { adminSettingsKeys } from '../../../hooks/useAdminSettings';
import TemplateEditorDialog from './TemplateEditorDialog';
import EmailPreviewDialog from './EmailPreviewDialog';

export default function TemplateManagerCard({ readOnly = false }: { readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplateKey, setPreviewTemplateKey] = useState<string | null>(null);

  // Fetch all templates
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: getEmailTemplates,
  });

  const [bulkRunning, setBulkRunning] = useState(false);

  /** A reminder step's wording/switch is the step's own: the Payment Reminders card must refetch. */
  const refreshStepViews = (templateKey: string) => {
    if (!templateKey.startsWith('step:')) return;
    queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reminderSteps() });
    queryClient.invalidateQueries({ queryKey: adminSettingsKeys.invoice() });
  };

  // Toggle template mutation
  const toggleMutation = useMutation({
    mutationFn: ({ templateKey, enabled }: { templateKey: string; enabled: boolean }) =>
      toggleEmailTemplate(templateKey, enabled),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      refreshStepViews(variables.templateKey);
      setSnackbar({ message: 'Template status updated', severity: 'success' });
      setTimeout(() => setSnackbar(null), 3000);
    },
    onError: (error: unknown) => {
      setSnackbar({ message: apiErrorMessage(error, 'Failed to update template status'), severity: 'error' });
      setTimeout(() => setSnackbar(null), 3000);
    },
  });

  // Group templates by category
  const groupedTemplates = templates?.reduce((acc, template) => {
    const category = template.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<string, EmailTemplate[]>) || {};

  // Filter templates by search term
  const filteredGroups = Object.entries(groupedTemplates).reduce((acc, [category, temps]) => {
    const filtered = temps.filter(
      (t) =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.key.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {} as Record<string, EmailTemplate[]>);

  const handleToggle = (templateKey: string, currentEnabled: boolean) => {
    toggleMutation.mutate({ templateKey, enabled: !currentEnabled });
  };

  const handleAccordionChange = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const handleOpenEditor = (templateKey: string) => {
    setSelectedTemplateKey(templateKey);
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setSelectedTemplateKey(null);
  };

  const handleEditorSave = () => {
    queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    if (selectedTemplateKey) refreshStepViews(selectedTemplateKey);
    setSnackbar({ message: 'Template customization saved', severity: 'success' });
    setTimeout(() => setSnackbar(null), 3000);
    handleCloseEditor();
  };

  const handleOpenPreview = (templateKey: string) => {
    setPreviewTemplateKey(templateKey);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewTemplateKey(null);
  };

  // Bulk enable/disable handlers
  // One after another, never in parallel: the server checks each switch against the others
  // (e.g. "two enabled steps more than a day apart"), so a parallel batch races those rules.
  // The Last Chance step is left alone by "Disable all": late fees may need it, so it is
  // switched off on its own.
  const handleBulkToggle = async (categoryTemplates: EmailTemplate[], enable: boolean) => {
    const targets = categoryTemplates.filter(
      (t) => t.enabled !== enable && !(!enable && t.kind === 'LAST_CHANCE')
    );
    setBulkRunning(true);
    let updated = 0;
    let refusedCount = 0;
    const refusals: string[] = [];
    for (const template of targets) {
      try {
        await toggleEmailTemplate(template.key, enable);
        updated += 1;
      } catch (error) {
        const message = apiErrorMessage(error, 'Failed to update template status');
        if (!refusals.includes(message)) refusals.push(message);
        refusedCount += 1;
      }
    }
    queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    if (targets.some((t) => t.key.startsWith('step:'))) {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reminderSteps() });
    }
    setBulkRunning(false);
    const skippedLastChance = !enable && categoryTemplates.some((t) => t.enabled && t.kind === 'LAST_CHANCE');
    const note = skippedLastChance ? ' The Last Chance step is switched off on its own, because late fees may need it.' : '';
    if (refusedCount > 0) {
      setSnackbar({
        message: `${updated} updated, ${refusedCount} not changed: ${refusals.join('; ')}.${note}`,
        severity: 'error',
      });
    } else {
      setSnackbar({ message: `${updated} updated.${note}`, severity: 'success' });
    }
    setTimeout(() => setSnackbar(null), 6000);
  };

  // Count enabled/disabled templates
  const totalCount = templates?.length || 0;
  const enabledCount = templates?.filter((t) => t.enabled).length || 0;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">Failed to load email templates</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Email Template Management
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Control which email notifications are sent to parents, teachers, and students
          </Typography>
          <Box display="flex" gap={2} alignItems="center" mt={2}>
            <Chip label={`${enabledCount} of ${totalCount} enabled`} color="primary" size="small" />
            <Chip label={`${totalCount - enabledCount} disabled`} size="small" />
          </Box>
        </Box>

        {snackbar && (
          <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
            {snackbar.message}
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {Object.keys(filteredGroups).length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            No templates found matching "{searchTerm}"
          </Typography>
        ) : (
          Object.entries(filteredGroups).map(([category, categoryTemplates]) => (
            <Accordion
              key={category}
              expanded={expandedCategory === category}
              onChange={() => handleAccordionChange(category)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {category}
                  </Typography>
                  <Chip
                    label={`${categoryTemplates.filter((t) => t.enabled).length}/${categoryTemplates.length}`}
                    size="small"
                    color={categoryTemplates.every((t) => t.enabled) ? 'success' : 'default'}
                    sx={{ ml: 'auto', mr: 2 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="flex-end"
                  mb={1}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleBulkToggle(categoryTemplates, true)}
                    disabled={readOnly || bulkRunning || toggleMutation.isPending || categoryTemplates.every((t) => t.enabled)}
                    fullWidth={isMobile}
                  >
                    Enable All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleBulkToggle(categoryTemplates, false)}
                    disabled={readOnly || bulkRunning || toggleMutation.isPending || categoryTemplates.every((t) => !t.enabled)}
                    fullWidth={isMobile}
                  >
                    Disable All
                  </Button>
                </Stack>
                {categoryTemplates.some((t) => t.kind === 'LAST_CHANCE') && (
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    &quot;Disable all&quot; leaves the Last Chance step on: late fees may need it, so switch it off on its own.
                  </Typography>
                )}
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: isMobile ? 300 : 500 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: isMobile ? 120 : 150 }}>Template Name</TableCell>
                        {!isMobile && <TableCell sx={{ minWidth: 120 }}>Customization</TableCell>}
                        <TableCell align="center" sx={{ minWidth: 70 }}>
                          Enabled
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 80 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryTemplates.map((template) => (
                        <TableRow key={template.key} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box>
                                <Typography variant="body2">{template.name}</Typography>
                                {/* A reminder step (#372) shows WHEN it goes out under its name. */}
                                {template.stepId && template.description && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {template.description}
                                  </Typography>
                                )}
                              </Box>
                              {template.description && !template.stepId && (
                                <Tooltip title={template.description} arrow>
                                  <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{ color: 'text.secondary', cursor: 'help' }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {template.isSubjectCustomized && (
                                  <Chip
                                    label="Subject"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {template.isPreheaderCustomized && (
                                  <Chip
                                    label="Preheader"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {template.isBodyCustomized && (
                                  <Chip
                                    label="Body"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {!template.isSubjectCustomized &&
                                  !template.isPreheaderCustomized &&
                                  !template.isBodyCustomized && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontStyle: 'italic' }}
                                    >
                                      Default
                                    </Typography>
                                  )}
                              </Stack>
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <Switch
                              checked={template.enabled}
                              onChange={() => handleToggle(template.key, template.enabled)}
                              disabled={readOnly || bulkRunning || toggleMutation.isPending}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Preview email">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPreview(template.key)}
                                color="default"
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!readOnly && <Tooltip title="Edit template customization">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditor(template.key)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        )}

        {/* Template Editor Dialog */}
        <TemplateEditorDialog
          open={editorOpen && !!selectedTemplateKey}
          templateKey={selectedTemplateKey || ''}
          templateName={templates?.find((t) => t.key === selectedTemplateKey)?.name}
          onClose={handleCloseEditor}
          onSave={handleEditorSave}
        />

        {/* Email Preview Dialog */}
        <EmailPreviewDialog
          open={previewOpen && !!previewTemplateKey}
          templateKey={previewTemplateKey || ''}
          onClose={handleClosePreview}
          readOnly={readOnly}
        />
      </CardContent>
    </Card>
  );
}
